'use client'

import { ProductCarousel } from '@/components/common'
import { ProductCarouselSkeleton } from '@/components/common/product-carousel/product-carousel-skeleton'
import { cn } from '@/lib/utils'
import { __cCollection2Service } from '@/services/client/__c.collection.service'
import { ProductFormatted } from '@/types/product'
import { useQuery } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import {
  COLLECTION_SLUG,
  DEFAULT_GENDER,
  GENDER_LINKS,
  RAIL_LIMIT,
  TGender,
  TRACKING_PREFIX,
} from './constants'
import {
  GenderTabs,
  SECTION_TITLE_CLASS,
  SECTION_X_CLASS,
  ViewAllLink,
} from './ui'

const TRACKING = `${TRACKING_PREFIX}__new-season`

type Props = {
  /** Sản phẩm của tab mặc định, lấy sẵn từ request chính của trang (server) */
  initialProducts: ProductFormatted[]
}

export function NewSeasonRail({ initialProducts }: Props) {
  const t = useTranslations('do_thu_dong_hardcode')
  const [gender, setGender] = useState<TGender>(DEFAULT_GENDER)
  const isDefaultTab = gender === DEFAULT_GENDER

  // Tab còn lại chỉ fetch khi người dùng bấm vào, react-query cache lại cho lần bấm sau
  const { data: lazyProducts, isLoading } = useQuery({
    queryKey: ['ldp-do-thu-dong', 'new-season', gender],
    queryFn: async () => {
      const data = await __cCollection2Service.getCollectionData(
        COLLECTION_SLUG,
        { gender_type: gender, limit: RAIL_LIMIT }
      )
      return data.products
    },
    enabled: !isDefaultTab,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const products = isDefaultTab ? initialProducts : (lazyProducts ?? [])

  const tabs = (
    <GenderTabs
      value={gender}
      onChange={setGender}
      labels={{ MALE: t('gender.male'), FEMALE: t('gender.female') }}
      trackingPrefix={TRACKING}
    />
  )
  const viewAll = (
    <ViewAllLink
      href={GENDER_LINKS[gender]}
      label={t('view_all')}
      trackingValue={`${TRACKING}__view-all__${gender.toLowerCase()}`}
    />
  )

  return (
    <section
      className={cn(
        'flex flex-col gap-5 py-10 lg:gap-10 lg:py-20',
        SECTION_X_CLASS
      )}
    >
      <div className='flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between'>
        <div className='flex items-end gap-10'>
          <h2 className={SECTION_TITLE_CLASS}>{t('new_season.title')}</h2>
          <div className='pb-1 max-lg:hidden'>{tabs}</div>
        </div>
        <div className='flex items-start justify-between pb-1 lg:hidden'>
          {tabs}
          {viewAll}
        </div>
        <div className='max-lg:hidden'>{viewAll}</div>
      </div>

      {!isDefaultTab && isLoading ? (
        <ProductCarouselSkeleton />
      ) : (
        <ProductCarousel
          key={gender}
          products={products}
          gaTrackingValue={`${TRACKING}__${gender.toLowerCase()}__product`}
          autoplay
        />
      )}
    </section>
  )
}
